/**
 * ImportEntryGate — shared entry-point chooser for new import flows.
 *
 * The single question asked up front when an admin starts a brand-new
 * import. Presents up to three cards:
 *
 *   1. Build a shell template
 *      Reusable blueprint only. No file, no mapping, no tenant. Mapping
 *      happens at import time inside the run wizard.
 *
 *   2. Build a full template from a sample file
 *      Reusable template with concrete column mappings. Upload a sample,
 *      map columns, save. No tenant — the file is used as a mapping
 *      guide only, not imported into any tenant.
 *
 *   3. Build a full template from a file and import
 *      Author a reusable template from the uploaded file, then
 *      immediately use that template to import the same file into
 *      the selected department. The saved template is system-wide
 *      — not bound to the department it was first used with.
 *      Requires a tenant for the final dry-run + commit step only.
 *
 * The component is deliberately host-agnostic:
 *
 *   - System-web supplies `tenantOptions` → Card 3 shows a picker and
 *     emits the chosen tenant id to `onChooseImport`.
 *   - Tenant-web supplies `implicitTenantId` → Card 3 hides the picker
 *     (the tenant is already known) and emits that id directly.
 *   - Any host can pass `showImportCard={false}` to hide Card 3
 *     entirely (e.g. a future "author only" surface).
 *
 * This matches the architectural principle: tenant context starts at
 * the data-dealing step (dry-run). Authoring a template is tenant-free.
 */

import { useState } from 'react';
import { FileSpreadsheet, LayoutTemplate, Rocket } from 'lucide-react';

/** A tenant the user can pick in Card 3 (system-web usage). */
export interface ImportEntryTenantOption {
  id: string;
  name: string;
}

export interface ImportEntryGateProps {
  /**
   * User picked Card 1 (shell) or Card 2 (bound). Parent is expected to
   * stay on the editor page and set the template mode accordingly.
   */
  onChooseTemplateMode: (mode: 'shell' | 'bound') => void;

  /**
   * User picked Card 3 and either picked a tenant or the host supplied
   * an implicit one. The parent is expected to transition the wizard
   * into bound-template authoring (mode='bound') and remember this
   * tenant id for the final "save & import" step at Review. The tenant
   * is NOT stored on the resulting template — it is only used to drive
   * the follow-on dry-run + commit against that tenant.
   * Not called if Card 3 is hidden.
   */
  onChooseImport?: (tenantId: string) => void;

  /**
   * Render the "Import a file now" card? Defaults to true. Set to false
   * when the host surface is pure template authoring (e.g. an embedded
   * modal where imports are a separate entry point).
   */
  showImportCard?: boolean;

  /**
   * System-web passes its `listTenants()` result here so Card 3 renders
   * a searchable picker. Ignored when `implicitTenantId` is set.
   */
  tenantOptions?: ImportEntryTenantOption[];

  /**
   * Loading state for `tenantOptions`. When true, the picker is
   * disabled and the placeholder reads "Loading departments…".
   */
  tenantsLoading?: boolean;

  /**
   * Tenant-web passes the currently-logged-in tenant here. Card 3 hides
   * the picker and submits this id directly when the user clicks
   * Continue. Takes precedence over `tenantOptions`.
   */
  implicitTenantId?: string;

  /**
   * Display name of the implicit tenant, if known. Only used for the
   * descriptive text on Card 3 ("Imports will go into <name>.") — a
   * missing name gracefully falls back to a generic phrase.
   */
  implicitTenantName?: string;

  /** Cancel the whole flow and return to the previous page. */
  onCancel?: () => void;
}

export default function ImportEntryGate({
  onChooseTemplateMode,
  onChooseImport,
  showImportCard = true,
  tenantOptions,
  tenantsLoading = false,
  implicitTenantId,
  implicitTenantName,
  onCancel,
}: ImportEntryGateProps) {
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');

  const hasImplicitTenant = !!implicitTenantId;
  const hasPicker = !hasImplicitTenant && !!tenantOptions;
  const cardColumns = showImportCard ? 'md:grid-cols-3' : 'md:grid-cols-2';

  const canSubmitImport = hasImplicitTenant
    ? true
    : hasPicker
      ? !!selectedTenantId
      : false;

  const handleImportSubmit = () => {
    if (!onChooseImport) return;
    if (hasImplicitTenant) {
      onChooseImport(implicitTenantId!);
      return;
    }
    if (!selectedTenantId) return;
    onChooseImport(selectedTenantId);
  };

  return (
    <div className="h-full w-full overflow-y-auto bg-gray-50">
      <div className="max-w-5xl w-full mx-auto px-6 py-10 space-y-6">
        <header className="space-y-2">
          <h2 className="text-lg font-semibold text-gray-900">
            What do you want to do?
          </h2>
          <p className="text-sm text-gray-600">
            Pick the intent for this session. Templates are reusable mapping
            definitions; a department is only required when you're actually
            importing data into one.
          </p>
        </header>

        <div className={`grid grid-cols-1 ${cardColumns} gap-4`}>
          {/* ---------- Card 1: Shell template ---------- */}
          <button
            type="button"
            data-testid="entry-gate-shell"
            onClick={() => onChooseTemplateMode('shell')}
            className="group border-2 border-gray-200 hover:border-orange-500 bg-white rounded-xl p-5 text-left transition-colors flex flex-col h-full"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-orange-50 group-hover:bg-orange-100 transition-colors">
                <LayoutTemplate className="h-5 w-5 text-orange-600" />
              </div>
              <h3 className="text-sm font-semibold text-gray-900">
                Build a shell template
              </h3>
            </div>
            <p className="text-xs text-gray-600 leading-relaxed mb-3">
              A reusable blueprint with no mappings yet. Best for recurring
              imports whose file shape changes each time (e.g. different
              vendors for the same data type).
            </p>
            <ul className="mt-auto space-y-1 text-[11px] text-gray-500">
              <li>• No file needed</li>
              <li>• No department needed</li>
              <li>• Mapping happens at import time</li>
            </ul>
          </button>

          {/* ---------- Card 2: Full template from sample ---------- */}
          <button
            type="button"
            data-testid="entry-gate-bound"
            onClick={() => onChooseTemplateMode('bound')}
            className="group border-2 border-gray-200 hover:border-blue-500 bg-white rounded-xl p-5 text-left transition-colors flex flex-col h-full"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-blue-50 group-hover:bg-blue-100 transition-colors">
                <FileSpreadsheet className="h-5 w-5 text-blue-600" />
              </div>
              <h3 className="text-sm font-semibold text-gray-900">
                Build a full template from a sample file
              </h3>
            </div>
            <p className="text-xs text-gray-600 leading-relaxed mb-3">
              Upload a representative file and map each column to a target
              field. The file is used only to guide mapping — no data is
              imported into any department.
            </p>
            <ul className="mt-auto space-y-1 text-[11px] text-gray-500">
              <li>• Upload a sample</li>
              <li>• No department needed</li>
              <li>• Save as a reusable template</li>
            </ul>
          </button>

          {/* ---------- Card 3: Import now + save template ---------- */}
          {showImportCard && (
            <div
              data-testid="entry-gate-import"
              className="border-2 border-gray-200 bg-white rounded-xl p-5 flex flex-col h-full"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-green-50">
                  <Rocket className="h-5 w-5 text-green-600" />
                </div>
                <h3 className="text-sm font-semibold text-gray-900">
                  Build a full template from a file and import
                </h3>
              </div>
              <p className="text-xs text-gray-600 leading-relaxed mb-3">
                {hasImplicitTenant
                  ? `Build a reusable template from an uploaded file, then use it to import that same file${implicitTenantName ? ` into ${implicitTenantName}` : ''}. The template is saved system-wide so any department can adopt it later.`
                  : 'Build a reusable template from an uploaded file, then use it to import that same file into the selected department. The template is saved system-wide — it is not bound to the department chosen here.'}
              </p>

              {/* Inline tenant picker — only when a picker is supplied
                  AND no implicit tenant is set. */}
              {hasPicker && (
                <div className="mt-2 space-y-1.5">
                  <label className="block text-[11px] font-semibold text-gray-700">
                    Department <span className="text-red-500">*</span>
                  </label>
                  <select
                    data-testid="entry-gate-tenant-select"
                    value={selectedTenantId}
                    onChange={e => setSelectedTenantId(e.target.value)}
                    disabled={tenantsLoading}
                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs text-gray-800 focus:outline-hidden focus:ring-1 focus:ring-green-500 disabled:bg-gray-50 disabled:text-gray-400"
                  >
                    <option value="">
                      {tenantsLoading ? 'Loading departments…' : 'Select a department…'}
                    </option>
                    {(tenantOptions ?? []).map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                  {!tenantsLoading && (tenantOptions ?? []).length === 0 && (
                    <p className="text-[11px] text-amber-700">
                      No departments available. Create a tenant first, then come back.
                    </p>
                  )}
                </div>
              )}

              <ul className="mt-3 mb-3 space-y-1 text-[11px] text-gray-500">
                {hasImplicitTenant ? (
                  <>
                    <li>• Build mapping from the uploaded file</li>
                    <li>• Template saved system-wide (reusable)</li>
                    <li>• Import into {implicitTenantName || 'the selected department'} at Review</li>
                  </>
                ) : (
                  <>
                    <li>• Build mapping from the uploaded file</li>
                    <li>• Template saved system-wide (reusable)</li>
                    <li>• Department required only for the final import step</li>
                  </>
                )}
              </ul>

              <button
                type="button"
                data-testid="entry-gate-import-continue"
                onClick={handleImportSubmit}
                disabled={!canSubmitImport}
                className="mt-auto inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue
              </button>
            </div>
          )}
        </div>

        {onCancel && (
          <div className="pt-2">
            <button
              type="button"
              onClick={onCancel}
              className="text-xs text-gray-500 hover:text-gray-800 underline underline-offset-2"
            >
              Cancel and return
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
